import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle, XCircle, Building2, Calendar, DollarSign, Loader2, User, Info, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { toastError } from "@/lib/toast-error";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface PendingDeal {
  id: string;
  accountName: string;
  opportunityName: string;
  ownerName?: string;
  ownerEmail?: string;
  closeDate?: string;
  mrr?: number;
  tcv?: number;
  notes?: string;
  products?: string[];
  supplierCount: number;
  approvalStatus?: string;
  createdAt: string;
  submittedBy?: { id: string; name?: string; email: string };
}

interface SupplierBundle {
  sfAccountId: string;
  accountName: string;
  routingBasis?: string;
  channelManagers: Array<{ id?: string; name: string; email: string; title?: string; supportedStates?: string }>;
  hasOverride: boolean;
  override?: { name: string; email: string; title?: string };
  error?: string;
}

function StatusBadge({ status }: { status?: string }) {
  if (status === "approved") return <Badge className="bg-green-100 text-green-800 border-green-300">Approved</Badge>;
  if (status === "denied") return <Badge className="bg-red-100 text-red-800 border-red-300">Denied</Badge>;
  return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Pending</Badge>;
}

function DealCard({ deal, onAction }: { deal: PendingDeal; onAction: (id: string, action: "approve" | "deny") => void }) {
  const [expanded, setExpanded] = useState(false);

  const { data: cmData, isLoading: isLoadingCMs } = useQuery<{ results: SupplierBundle[] }>({
    queryKey: [`${BASE}/api/local-deals/${deal.id}/channel-managers`],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/local-deals/${deal.id}/channel-managers`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json())?.error ?? "Failed");
      return r.json();
    },
    enabled: expanded,
    staleTime: 0,
  });

  return (
    <Card className="border">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate">{deal.opportunityName}</h3>
              <StatusBadge status={deal.approvalStatus} />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{deal.accountName}</span>
              {deal.closeDate && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{deal.closeDate}</span>}
              {deal.mrr && <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />${Number(deal.mrr).toLocaleString()} MRR</span>}
              <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{deal.supplierCount} supplier{deal.supplierCount !== 1 ? "s" : ""}</span>
            </div>
            {deal.submittedBy && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <User className="h-3 w-3" />
                Submitted by {deal.submittedBy.name ?? deal.submittedBy.email} · {new Date(deal.createdAt).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {deal.approvalStatus === "pending" && (
              <>
                <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => onAction(deal.id, "approve")}>
                  <CheckCircle className="mr-1.5 h-3.5 w-3.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => onAction(deal.id, "deny")}>
                  <XCircle className="mr-1.5 h-3.5 w-3.5" /> Deny
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Hide" : "Details"}
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 space-y-4 border-t pt-4">
            {deal.notes && (
              <p className="text-sm text-muted-foreground bg-muted/40 rounded p-3">{deal.notes}</p>
            )}
            {deal.products && deal.products.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {deal.products.map((p, i) => <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>)}
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold mb-2">Supplier Channel Managers</h4>
              {isLoadingCMs ? (
                <div className="space-y-2">{[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
              ) : (cmData?.results ?? []).map((bundle) => (
                <div key={bundle.sfAccountId} className="border rounded p-3 mb-2 text-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{bundle.accountName}</span>
                    {bundle.routingBasis && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Info className="h-3 w-3" /> {bundle.routingBasis}
                      </span>
                    )}
                  </div>
                  {bundle.hasOverride && bundle.override && (
                    <div className="bg-primary/5 border border-primary/20 rounded p-2 mb-2 text-xs">
                      <span className="font-medium text-primary">Override: </span>
                      {bundle.override.name} &lt;{bundle.override.email}&gt;{bundle.override.title ? ` — ${bundle.override.title}` : ""}
                    </div>
                  )}
                  {bundle.error ? (
                    <p className="text-destructive text-xs">{bundle.error}</p>
                  ) : bundle.channelManagers.length === 0 ? (
                    <p className="text-muted-foreground text-xs">No channel managers</p>
                  ) : (
                    <ul className="space-y-1">
                      {bundle.channelManagers.map((cm, i) => (
                        <li key={cm.id ?? i} className="flex items-start gap-2">
                          <User className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span>{cm.name} &lt;{cm.email}&gt;{cm.title ? ` — ${cm.title}` : ""}{cm.supportedStates ? ` · ${cm.supportedStates}` : ""}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ApprovalsPage() {
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] = useState<{ id: string; action: "approve" | "deny" } | null>(null);
  const [actionNotes, setActionNotes] = useState("");

  const { data, isLoading } = useQuery<{ results: PendingDeal[] }>({
    queryKey: [`${BASE}/api/local-deals/pending`],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/local-deals/pending`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json())?.error ?? "Failed");
      return r.json();
    },
    staleTime: 30_000,
  });

  const actionMutation = useMutation({
    mutationFn: async ({ id, action, notes }: { id: string; action: string; notes: string }) => {
      const r = await fetch(`${BASE}/api/local-deals/${id}/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approvalNotes: notes || undefined }),
      });
      if (!r.ok) throw new Error((await r.json())?.error ?? "Failed");
      return r.json();
    },
    onSuccess: (_, { action }) => {
      toast.success(action === "approve" ? "Deal approved and team notified" : "Deal denied");
      queryClient.invalidateQueries({ queryKey: [`${BASE}/api/local-deals`] });
      queryClient.invalidateQueries({ queryKey: [`${BASE}/api/local-deals/pending`] });
      setPendingAction(null);
      setActionNotes("");
    },
    onError: (err: unknown) => toastError(err, "Action failed"),
  });

  const deals = data?.results ?? [];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Link href="/deals">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Deal Approvals</h1>
          <p className="text-muted-foreground">Review and approve submitted deal registrations.</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : deals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-semibold text-lg">All clear</h3>
            <p className="text-muted-foreground text-sm mt-1">No pending deals awaiting approval.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{deals.length} deal{deals.length !== 1 ? "s" : ""} awaiting approval</p>
          {deals.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              onAction={(id, action) => {
                setPendingAction({ id, action });
                setActionNotes("");
              }}
            />
          ))}
        </div>
      )}

      <Dialog open={Boolean(pendingAction)} onOpenChange={() => { setPendingAction(null); setActionNotes(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingAction?.action === "approve" ? "Approve Deal" : "Deny Deal"}
            </DialogTitle>
            <DialogDescription>
              {pendingAction?.action === "approve"
                ? "The internal team will be notified by email with deal details and channel managers."
                : "The deal will be marked as denied. No notification will be sent."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="action-notes">Notes (optional)</Label>
            <Textarea
              id="action-notes"
              placeholder="Add any notes about this decision..."
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAction(null)}>Cancel</Button>
            <Button
              variant={pendingAction?.action === "deny" ? "destructive" : "default"}
              disabled={actionMutation.isPending}
              onClick={() => {
                if (!pendingAction) return;
                actionMutation.mutate({ id: pendingAction.id, action: pendingAction.action, notes: actionNotes });
              }}
            >
              {actionMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {pendingAction?.action === "approve" ? "Approve" : "Deny"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
